<template>
    <div class="mx-sm-6">
        <h4 class="mb-3 monthly-payment-label">Monthly Payment</h4>
        <v-row>
            <!-- Minimum Monthly Payment -->
            <v-col cols="12" md="6" class="align-content-center pb-0 py-md-4">
                <v-select
                    v-model="filters.minMonthlyPaymentAmount"
                    :items="minMonthlyPaymentAmountOptions"
                    item-text="title"
                    item-value="value"
                    append-icon="$vuetify.icon.chevronDown"
                    outlined
                    label="Minimum"
                    height="56px"
                    @change="sortAndFilterPaymentPlans"
                />
            </v-col>
            <!-- Maximum Monthly Payment -->
            <v-col cols="12" md="6" class="align-content-center align-items-center pt-0 py-md-4">
                <v-select
                    v-model="filters.maxMonthlyPaymentAmount"
                    :items="maxMonthlyPaymentAmountOptions"
                    item-text="title"
                    item-value="value"
                    append-icon="$vuetify.icon.chevronDown"
                    outlined
                    label="Maximum"
                    height="56px"
                    @change="sortAndFilterPaymentPlans"
                />
            </v-col>
        </v-row>
        <h4 class="full-financed-amount-label">Only show the options with...</h4>
        <v-checkbox
            v-model="filters.onlyShowFullFinancedAmount"
            label="Full Financed Amount"
            @change="sortAndFilterPaymentPlans"
        ></v-checkbox>
        <!--  Filter Product Type -->
        <v-radio-group v-model="filters.productType" @change="sortAndFilterPaymentPlans">
            <v-radio value="ALL" label="All" />
            <div v-for="filter in planTypeFilters" :key="filter.filterKey" class="pt-tooltip-group">
                <v-radio :value="filter.filterKey" :label="$t(`prequalify.tooltip.${filter.filterKey}.header`)" />
                <v-tooltip right content-class="pt-tooltip">
                    <template #activator="{ on, attrs }">
                        <span class="pt-activator" v-bind="attrs" v-on="on"> ? </span>
                    </template>
                    <span class="pt-tooltip-header">
                        {{ $t(`prequalify.tooltip.${filter.filterKey}.header`) }}
                    </span>
                    <span class="pt-tooltip-body">
                        {{ $t(`prequalify.tooltip.${filter.filterKey}.body`) }}
                    </span>
                </v-tooltip>
            </div>
            <template #label>
                <span class="product-type-label">Product Type</span>
            </template>
        </v-radio-group>
        <!--        Reset Filter       -->
        <span class="px-4 py-4 reset-filter-button" @click="resetFilters">Reset Filter</span>
        <v-divider class="my-5 mx-0"></v-divider>
        <!--        Provider        -->
        <h4 class="full-financed-amount-label">Provider</h4>
        <h4 class="finance-header">Amount to Finance: {{ formattedPurchaseAmountWhole }}</h4>
        <div class="provider-filters">
            <v-checkbox
                v-for="providerKey in providerKeys"
                :id="`chkbox-${providerKey.key}`"
                :key="`chkbox-${providerKey.key}`"
                v-model="filters.providerKeys[providerKey.key]"
                @change="sortAndFilterPaymentPlans"
            >
                <template slot="label">
                    <!-- pf = provider filter -->
                    <span class="pf-header"> {{ providerKey.name }} </span>
                    <span class="pf-body">
                        Prequalified Amount:
                        {{ formatWholeCurrency(providerKey.approvedAmountCents, "cents") }}
                    </span>
                </template>
            </v-checkbox>
        </div>
    </div>
</template>

<script>
    import { providerState, sessionState } from "@/state"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
    import Providers from "@/model/provider/Providers"
    import PaymentPlanType from "@/model/apply/PaymentPlanType"
    import { formatWholeCurrency } from "@/util/currency"
    import { isAnyPreapprovalCode, isApprovedCode } from "@/model/apply/ApplyResponseCode"
    import ProviderKey from "@/model/provider/ProviderKey"
    import ProductType from "@/model/apply/ProductType"
    import ProviderId from "@/model/provider/ProviderId"

    export default {
        components: {},
        props: {
            value: {
                type: Object,
            },
            formattedPurchaseAmountWhole: {
                type: String,
            },
            planTypeFilters: {
                type: Array,
            },
        },
        data() {
            const responses = providerState
                .getAllPreAuthResponses()
                .filter(response => this.isValidPreauthResponse(response))
            return {
                responses,
                formatWholeCurrency,

                minMonthlyPaymentAmountOptions: [
                    { title: "$0", value: 0 },
                    { title: "$100", value: 10000 },
                    { title: "$200", value: 20000 },
                    { title: "$300", value: 30000 },
                    { title: "$400", value: 40000 },
                    { title: "$500", value: 50000 },
                    { title: "$600", value: 60000 },
                    { title: "$700", value: 70000 },
                    { title: "$800", value: 80000 },
                    { title: "$900", value: 90000 },
                ],
                maxMonthlyPaymentAmountOptions: [
                    { title: "$1000+", value: 9999999 },
                    { title: "$900", value: 90000 },
                    { title: "$800", value: 80000 },
                    { title: "$700", value: 70000 },
                    { title: "$600", value: 60000 },
                    { title: "$500", value: 50000 },
                    { title: "$400", value: 40000 },
                    { title: "$300", value: 30000 },
                ],
            }
        },
        computed: {
            filters: {
                get: function () {
                    return this.value
                },
                set: function (value) {
                    this.$emit("value-change", value)
                },
            },
            providerKeys() {
                const providerKeys = []
                this.responses.forEach(function (response) {
                    const excludedProviderKeys = providerState.excludedProviders.providerKeys
                        ? providerState.excludedProviders.providerKeys
                        : []
                    const dynamicKey = DynamicProviderKey.extractProviderKey(response.providerKey)
                    const paymentPlans = providerState.responses[response.providerKey]?.paymentPlans
                    const subLender = Providers[dynamicKey].subLender

                    const isSubLender =
                        subLender &&
                        paymentPlans?.filter(
                            paymentPlan => paymentPlan.typeId === PaymentPlanType.SUB_LENDER_FIXED_LOAN
                        ).length > 0

                    let providerName = Providers[dynamicKey]?.financingName
                    if (isSubLender) {
                        providerName = subLender.financingName
                    }

                    if (providerName && paymentPlans?.length && !excludedProviderKeys.includes(response.providerKey)) {
                        const isExistingAccount = response.existingAccount && response.account
                        const amountCents = isExistingAccount
                            ? response.account.creditLimitCents
                            : response.approvedAmountCents
                        providerKeys.push({
                            key: response.providerKey,
                            name: providerName,
                            approvedAmountCents: amountCents,
                        })
                    }

                    if (
                        response.providerKey.includes(ProviderKey.SYNCHRONY_STANDARD) &&
                        paymentPlans?.length &&
                        paymentPlans.some(plan => plan.providerProductType === ProductType.LOAN)
                    ) {
                        providerKeys.push({
                            key: "paylater",
                            name: "CareCredit Pay Later Loan",
                            approvedAmountCents: response.approvedAmountCents
                        })
                    }
                })
                return providerKeys
            },
        },
        methods: {
            isValidPreauthResponse(response) {
                const hasValidCode =
                    isAnyPreapprovalCode(response.responseCode) ||
                    (response.existingAccount && isApprovedCode(response.responseCode))
                if (!hasValidCode) return false
                if (sessionState.providerKey === response.providerKey) return true
                if (providerState.getApplyResponse(response.providerKey)) return false
                const sessionProvider = providerState.providers.find(
                    provider => provider.providerKey === sessionState.providerKey
                )
                if (!sessionProvider) return false
                const responseProvider = providerState.providers.find(
                    provider => provider.providerKey === response.providerKey
                )
                if (!responseProvider) return false
                return sessionProvider.cascadeGroup === responseProvider.cascadeGroup
            },
            sortAndFilterPaymentPlans(e) {
                this.$emit("change", e)
            },
            resetFilters(e) {
                this.$emit("reset", e)
            },
        },
    }
</script>

<style lang="scss">
    // Some styles for this view are also found in components.scss
    .provider-filters {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: 8px;
        .v-label {
            display: flex !important;
            flex-direction: column;
            flex-wrap: wrap;
            align-content: flex-start;
            align-items: flex-start !important;
            justify-content: center;
            .pf-header {
                display: flex;
                text-align: left;

                color: $grey-darken-2;
                font-size: 16px;
                font-style: normal;
                font-weight: 600;
                line-height: 24px; /* 150% */
            }
            .pf-body {
                display: flex;

                text-align: left;

                color: $grey-darken-2;
                font-size: 14px;
                font-style: normal;
                font-weight: 350;
                line-height: 20px; /* 142.857% */
            }
        }
    }
</style>

<style scoped lang="scss">
    .pt-tooltip-body {
        color: $grey-darken-2;
        font-size: 18px;
        font-style: normal;
        font-weight: 350;
        line-height: 28px; /* 155.556% */
    }
    .pt-tooltip-header {
        color: $primary-color;
        font-size: 26px;
        font-style: normal;
        font-weight: 600;
        line-height: 32px; /* 123.077% */
        letter-spacing: -0.5px;
    }
    .pt-tooltip-group {
        display: inline-flex;
    }
    .pt-tooltip {
        opacity: 1 !important;
        display: flex;
        width: 616px;
        padding: 40px;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
        flex-shrink: 0;
        border-radius: 8px;
        background-color: #fff;
        box-shadow: 0px 15px 12px 0px rgba(0, 0, 0, 0.22), 0px 19px 38px 0px rgba(0, 0, 0, 0.3);
    }
    .pt-activator {
        margin-left: 10px;
        text-align: center;
        width: 24px;
        height: 24px;
        background-color: #696e72;
        color: #fff;
        border-radius: 15px;
    }

    .reset-filter-button {
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        color: $secondary-color;
        border-radius: 5px;
        margin-left: -16px;
        &:hover {
            background-color: darken(#fff, 5%);
            cursor: pointer;
        }
    }

    .monthly-payment-label {
        color: $grey-darken-2;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px; /* 155.556% */
    }

    .product-type-label {
        color: $grey-darken-2;
        font-size: 16px;
        font-style: normal;
        font-weight: 350;
        line-height: 24px; /* 150% */
    }

    .full-financed-amount-label {
        color: $grey-darken-2;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px; /* 155.556% */
    }
    .finance-header {
        color: $grey-darken-2;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 150% */
    }
</style>
